import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  EVENT_CATEGORIES,
  createEventType,
  deleteEventLog,
  deleteEventType,
  emitEvent,
  eventCategoryMeta,
  fetchEventStats,
  listEventLog,
  listEventTypes,
  markEventProcessed,
  setEventTypeActive,
  type CoreEventLog,
  type CoreEventType,
} from '@/lib/coreEvents';
import Modal from '@/pages/dashboard/core/components/Modal';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

export default function EventsTab({ org }: Props) {
  const [types, setTypes] = useState<CoreEventType[]>([]);
  const [log, setLog] = useState<CoreEventLog[]>([]);
  const [stats, setStats] = useState<{ total: number; unprocessed: number }>({ total: 0, unprocessed: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ eventKey: '', entityType: 'customer', entityId: '' });
  const [typeOpen, setTypeOpen] = useState(false);
  const [typeForm, setTypeForm] = useState({ key: '', category: 'customer', label: '' });
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [t, l, s] = await Promise.all([listEventTypes(), listEventLog(org.id, { limit: 100 }), fetchEventStats(org.id)]);
      setTypes(t);
      setLog(l);
      setStats(s);
      if (!form.eventKey && t.length > 0) setForm((f) => ({ ...f, eventKey: t[0].key }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [org.id, form.eventKey]);

  useEffect(() => { load(); }, [load]);

  const typesByCategory = useMemo(() => {
    const map: Record<string, CoreEventType[]> = {};
    types.forEach((t) => {
      const c = t.category || 'other';
      if (!map[c]) map[c] = [];
      map[c].push(t);
    });
    return map;
  }, [types]);

  const filteredLog = useMemo(
    () => (catFilter === 'all' ? log : log.filter((l) => (l.event_key || '').startsWith(`${catFilter}.`))),
    [log, catFilter],
  );

  const emit = async () => {
    if (!form.eventKey) return;
    setSaving(true);
    try {
      await emitEvent({
        tenantId: org.tenant_id,
        organizationId: org.id,
        eventKey: form.eventKey,
        entityType: form.entityType || null,
        entityId: form.entityId.trim() || null,
        source: 'manual',
      });
      setOpen(false);
      setForm({ ...form, entityId: '' });
      load();
    } finally {
      setSaving(false);
    }
  };

  const toggleType = async (t: CoreEventType) => {
    setBusyId(t.id);
    try { await setEventTypeActive(t.id, !t.is_active); load(); } finally { setBusyId(null); }
  };

  const removeType = async (t: CoreEventType) => {
    setBusyId(t.id);
    try { await deleteEventType(t.id); load(); } finally { setBusyId(null); }
  };

  const addType = async () => {
    if (!typeForm.key.trim()) return;
    setSaving(true);
    try {
      await createEventType({ key: typeForm.key.trim(), category: typeForm.category, label: typeForm.label.trim() || typeForm.key.trim() });
      setTypeOpen(false);
      setTypeForm({ key: '', category: 'customer', label: '' });
      load();
    } finally {
      setSaving(false);
    }
  };

  const process = async (l: CoreEventLog) => {
    setBusyId(l.id);
    try { await markEventProcessed(l.id, !l.processed); load(); } finally { setBusyId(null); }
  };

  const removeLog = async (l: CoreEventLog) => {
    setBusyId(l.id);
    try { await deleteEventLog(l.id); load(); } finally { setBusyId(null); }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-xs text-foreground-500 max-w-3xl">
          Event Bus : les modules émettent des événements (customer.created, appointment.confirmed, invoice.paid…) qui peuvent déclencher
          <span className="font-semibold text-foreground-700"> notifications, workflows, marketing, analytics et actions AI</span>.
        </p>
        <div className="flex items-center gap-2">
          <button onClick={() => setTypeOpen(true)} className="px-3.5 py-2 rounded-full bg-background-100 text-foreground-700 text-xs font-medium whitespace-nowrap hover:bg-background-200/70 transition-colors cursor-pointer"><i className="ri-add-line"></i> Type d’événement</button>
          <button onClick={() => setOpen(true)} className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"><i className="ri-send-plane-line"></i> Émettre</button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-3"><i className="ri-flashlight-line text-lg text-primary-600"></i><p className="text-lg font-bold text-foreground-900 mt-1">{stats.total}</p><p className="text-xs text-foreground-500">Événements journalisés</p></div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-3"><i className="ri-time-line text-lg text-accent-600"></i><p className="text-lg font-bold text-foreground-900 mt-1">{stats.unprocessed}</p><p className="text-xs text-foreground-500">Non traités</p></div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-3"><i className="ri-list-check text-lg text-secondary-600"></i><p className="text-lg font-bold text-foreground-900 mt-1">{types.length}</p><p className="text-xs text-foreground-500">Types d’événement</p></div>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-16"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
      ) : (
        <>
          <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2"><span className="w-8 h-8 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center"><i className="ri-list-check"></i></span><h4 className="text-sm font-semibold text-foreground-900">Types d’événement</h4></div>
            <div className="p-3 space-y-3">
              {Object.entries(typesByCategory).map(([cat, list]) => (
                <div key={cat}>
                  <p className="text-[11px] font-semibold text-foreground-600 mb-1.5 flex items-center gap-1.5"><i className={eventCategoryMeta(cat).icon}></i>{eventCategoryMeta(cat).label}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {list.map((t) => (
                      <span key={t.id} className={`inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full border text-[11px] ${t.is_active ? 'border-background-200/70 bg-background-100 text-foreground-700' : 'border-background-200/50 bg-background-50 text-foreground-400'}`}>
                        <code>{t.key}</code>
                        <button onClick={() => toggleType(t)} disabled={busyId === t.id} title={t.is_active ? 'Désactiver' : 'Activer'} className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-background-200/60 transition-colors cursor-pointer disabled:opacity-50"><i className={t.is_active ? 'ri-toggle-fill text-primary-600' : 'ri-toggle-line'}></i></button>
                        <button onClick={() => removeType(t)} disabled={busyId === t.id} className="w-5 h-5 flex items-center justify-center rounded-full text-foreground-400 hover:text-red-600 transition-colors cursor-pointer disabled:opacity-50"><i className="ri-close-line text-[11px]"></i></button>
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-1 bg-background-100 rounded-full p-1 w-fit">
            <button onClick={() => setCatFilter('all')} className={`px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${catFilter === 'all' ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}>Tous</button>
            {EVENT_CATEGORIES.map((c) => (
              <button key={c.key} onClick={() => setCatFilter(c.key)} className={`px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${catFilter === c.key ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}>{c.label}</button>
            ))}
          </div>

          <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2"><span className="w-8 h-8 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center"><i className="ri-history-line"></i></span><h4 className="text-sm font-semibold text-foreground-900">Journal des événements</h4></div>
            {filteredLog.length === 0 ? (
              <p className="px-4 py-6 text-xs text-foreground-400 text-center">Aucun événement journalisé.</p>
            ) : (
              <div className="divide-y divide-background-200/60 max-h-[520px] overflow-y-auto">
                {filteredLog.map((l) => {
                  const meta = eventCategoryMeta((l.event_key || '').split('.')[0]);
                  return (
                    <div key={l.id} className="flex items-center gap-3 px-4 py-2.5">
                      <span className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${meta.className}`}><i className={`${meta.icon} text-sm`}></i></span>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-foreground-800 truncate">{l.event_key}</p>
                        <p className="text-[11px] text-foreground-400 truncate">
                          {l.entity_type ? `${l.entity_type} #${l.entity_id}` : 'sans entité'}
                          {l.source ? ` · ${l.source}` : ''}
                          {l.created_at ? ` · ${new Date(l.created_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}
                        </p>
                      </div>
                      <button onClick={() => process(l)} disabled={busyId === l.id} title="Marquer traité / non traité" className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap cursor-pointer disabled:opacity-50 ${l.processed ? 'bg-primary-100 text-primary-900' : 'bg-accent-100 text-accent-900'}`}>{l.processed ? 'traité' : 'à traiter'}</button>
                      <button onClick={() => removeLog(l)} disabled={busyId === l.id} className="w-7 h-7 flex items-center justify-center rounded-full text-foreground-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-50 flex-shrink-0"><i className="ri-delete-bin-line"></i></button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {open && (
        <Modal
          title="Émettre un événement"
          onClose={() => setOpen(false)}
          maxWidth="max-w-lg"
          footer={
            <>
              <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={emit} disabled={saving || !form.eventKey} className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-send-plane-line"></i>} Émettre
              </button>
            </>
          }
        >
          <div className="space-y-3">
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Événement</span>
              <select className={INPUT} value={form.eventKey} onChange={(e) => setForm({ ...form, eventKey: e.target.value })}>
                {types.map((t) => <option key={t.id} value={t.key}>{t.label || t.key} — {t.key}</option>)}
              </select>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Entité (optionnel)</span>
                <select className={INPUT} value={form.entityType} onChange={(e) => setForm({ ...form, entityType: e.target.value })}>
                  <option value="">—</option>
                  <option value="customer">Client</option>
                  <option value="order">Commande</option>
                  <option value="booking">Réservation</option>
                  <option value="appointment">Rendez-vous</option>
                  <option value="invoice">Facture</option>
                </select>
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Identifiant</span>
                <input className={INPUT} value={form.entityId} onChange={(e) => setForm({ ...form, entityId: e.target.value })} placeholder="Ex : 100234" />
              </label>
            </div>
          </div>
        </Modal>
      )}

      {typeOpen && (
        <Modal
          title="Nouveau type d’événement"
          onClose={() => setTypeOpen(false)}
          maxWidth="max-w-lg"
          footer={
            <>
              <button onClick={() => setTypeOpen(false)} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={addType} disabled={saving || !typeForm.key.trim()} className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>} Créer
              </button>
            </>
          }
        >
          <div className="space-y-3">
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Clé (domaine.action)</span>
              <input className={INPUT} value={typeForm.key} onChange={(e) => setTypeForm({ ...typeForm, key: e.target.value })} placeholder="Ex : project.completed" />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Catégorie</span>
              <select className={INPUT} value={typeForm.category} onChange={(e) => setTypeForm({ ...typeForm, category: e.target.value })}>
                {EVENT_CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Libellé</span>
              <input className={INPUT} value={typeForm.label} onChange={(e) => setTypeForm({ ...typeForm, label: e.target.value })} placeholder="Ex : Projet terminé" />
            </label>
          </div>
        </Modal>
      )}
    </div>
  );
}